/**
 * Hot Plate · ordering planner.
 *
 * Ported from the quotinc house planner (`lib/notes-logic.ts::planMove`,
 * the scheme the porting spec recommends over the board's own whole-column
 * renumber).
 *
 * Why it matters
 * ──────────────
 * The pre-2026-07-31 board recomputed EVERY row of both touched columns as
 * `(i+1)*100` on every drop and POSTed the lot; the backend then issued one
 * sequential PostgREST PATCH per item. A drop on a 40-card board could be
 * 60+ round-trips inside one request, and on an admin's cross-team union it
 * rewrote other teams' ordering as a side effect.
 *
 * The planner instead looks for a gap between the two neighbours the card
 * lands between and, when one exists, emits a SINGLE row patch. Only when
 * the integer space between neighbours is exhausted does it fall back to a
 * whole-section renumber. In practice, with `POSITION_STEP = 1000`, that is
 * ~10 consecutive drops into the identical slot before one renumber.
 *
 * Everything here is pure — no React, no fetch, no dnd-kit.
 */

import type { HotPlateColumn, ReorderItem } from './types';

/** Seed spacing. 1000 (not 10) so midpoints survive real usage. */
export const POSITION_STEP = 1000;

/** int4 ceiling — matches the API clamp and the Postgres column type. */
export const POSITION_MAX = 2147483647;

export interface Positioned {
  id: string;
  position: number;
}

export type MoveEdge = 'before' | 'after';

export type MovePlan =
  | { kind: 'noop' }
  /** One row moves; everything else keeps its position. */
  | { kind: 'patch'; position: number }
  /** Gap exhausted — rewrite the whole section in `ids` order. */
  | { kind: 'renumber'; ids: string[] };

/**
 * Stable position comparator.
 *
 * Ties break on `id` so two rows that share a position (legacy data, or the
 * `Date.now() % 1e6` collisions the quotinc Backhub board tolerated) always
 * sort the same way. Without this, `computeBoard` could shuffle equal-position
 * cards between renders and the drag preview would flicker.
 */
export function comparePosition(a: Positioned, b: Positioned): number {
  const d = (a.position ?? 0) - (b.position ?? 0);
  if (d !== 0) return d;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Plan the move of `dragId` into `insertIdx` of `section` (the target
 * column, position-sorted, INCLUDING the dragged row when it already lives
 * there). `insertIdx` is an index into the section *without* the dragged
 * row — which is exactly what a drop indicator describes.
 *
 * @param sameSection true when the card is not changing column. A move
 *   inside a section that lands where the card already is, is a `noop`;
 *   the identical index across columns is a real move and must be written.
 */
export function planInsertAt(
  section: readonly Positioned[],
  dragId: string,
  insertIdx: number,
  sameSection: boolean,
): MovePlan {
  const withoutDrag = section.filter((n) => n.id !== dragId);
  const idx = Math.max(0, Math.min(insertIdx, withoutDrag.length));

  if (sameSection) {
    const currentIdx = section.findIndex((n) => n.id === dragId);
    if (currentIdx !== -1) {
      // How many rows currently sit above the dragged one? If that equals
      // the requested slot, nothing changes — write nothing.
      const before = section.slice(0, currentIdx).filter((n) => n.id !== dragId).length;
      if (before === idx) return { kind: 'noop' };
    }
  }

  const prev = idx > 0 ? withoutDrag[idx - 1] : null;
  const next = idx < withoutDrag.length ? withoutDrag[idx] : null;

  if (prev && next) {
    const gap = next.position - prev.position;
    if (gap >= 2) return { kind: 'patch', position: prev.position + Math.floor(gap / 2) };
  } else if (prev) {
    const candidate = prev.position + POSITION_STEP;
    if (candidate <= POSITION_MAX) return { kind: 'patch', position: candidate };
  } else if (next) {
    if (next.position >= POSITION_STEP + 1) {
      return { kind: 'patch', position: next.position - POSITION_STEP };
    }
    if (next.position >= 2) return { kind: 'patch', position: Math.floor(next.position / 2) };
  } else {
    // Empty target section — the card is the only row in it.
    return { kind: 'patch', position: POSITION_STEP };
  }

  const ids = [
    ...withoutDrag.slice(0, idx).map((n) => n.id),
    dragId,
    ...withoutDrag.slice(idx).map((n) => n.id),
  ];
  return { kind: 'renumber', ids };
}

/**
 * Plan a move relative to a neighbour card — the shape a drop indicator
 * naturally produces ("insert before/after this card").
 */
export function planMove(
  section: readonly Positioned[],
  dragId: string,
  overId: string,
  edge: MoveEdge,
  sameSection: boolean,
): MovePlan {
  const withoutDrag = section.filter((n) => n.id !== dragId);
  const overIdx = withoutDrag.findIndex((n) => n.id === overId);
  if (overIdx === -1) return { kind: 'noop' };
  return planInsertAt(section, dragId, edge === 'after' ? overIdx + 1 : overIdx, sameSection);
}

/** Plan a drop at the very end of a section (empty column / below the last card). */
export function planMoveToEnd(
  section: readonly Positioned[],
  dragId: string,
  sameSection: boolean,
): MovePlan {
  const withoutDrag = section.filter((n) => n.id !== dragId);
  return planInsertAt(section, dragId, withoutDrag.length, sameSection);
}

/** Position for a brand-new card appended to `section`. */
export function nextAppendPosition(section: readonly Positioned[]): number {
  if (section.length === 0) return POSITION_STEP;
  const max = section.reduce((m, n) => Math.max(m, n.position ?? 0), 0);
  const candidate = max + POSITION_STEP;
  return candidate <= POSITION_MAX ? candidate : POSITION_MAX;
}

/** Turn a `renumber` plan into the batch the reorder endpoint expects. */
export function renumberItems(ids: readonly string[], column: HotPlateColumn): ReorderItem[] {
  return ids.map((id, i) => ({ id, columnKey: column, position: (i + 1) * POSITION_STEP }));
}

/**
 * Turn any plan into the wire batch. A `patch` is a one-item batch — the
 * reorder endpoint is already a per-row PATCH loop, so a single item is
 * literally one UPDATE.
 */
export function planToItems(
  plan: MovePlan,
  dragId: string,
  column: HotPlateColumn,
): ReorderItem[] {
  switch (plan.kind) {
    case 'noop':
      return [];
    case 'patch':
      return [{ id: dragId, columnKey: column, position: plan.position }];
    case 'renumber':
      return renumberItems(plan.ids, column);
  }
}
